import { ExternalLink, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { buttonVariants } from '@/shared/components/ui/button'
import { useFormatters } from '@/shared/lib/formatting'
import type { UploadSession } from '../types'

/**
 * A link that shares one file to read, such as an inspection's report: the
 * file and when the link stops working. Nothing to upload or submit.
 */
export function SharedDocument({
  session,
}: {
  session: UploadSession & { document: NonNullable<UploadSession['document']> }
}) {
  const { t } = useTranslation()
  const { locale } = useFormatters()
  const until = new Date(session.expires_at).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  return (
    <div className="panel-arrive" data-testid="shared-document">
      <h1 className="mb-2 font-display text-[26px] font-light leading-tight text-[var(--ink)]">
        {session.presentation.title}
      </h1>
      <p className="clara-body mb-6 text-[var(--ink-2)]">
        {t('upload.document.intro', 'Shared with you to read.')}
      </p>
      <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <div className="flex items-center gap-3">
          <FileText size={18} className="shrink-0 text-[var(--ink-3)]" />
          <span className="clara-body min-w-0 truncate text-[var(--ink)]">
            {session.document.file_name}
          </span>
        </div>
        <a
          href={session.document.url}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonVariants({ className: 'mt-4 w-full gap-1.5' })}
          data-testid="shared-document-open"
        >
          {t('upload.document.open', 'Open')}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <p className="clara-small mt-4 text-center text-[var(--ink-3)]">
        {t('upload.document.until', 'This link works until {{date}}.', { date: until })}
      </p>
    </div>
  )
}
